/**
 * The live-service query + mutation layer. Timeline + floor both key under
 * `["venue", venueId, "bookings", "live", …]` — a SUBTREE of the `bookings` resource — so the shell's
 * `eventQueryKeyMap` (`booking.*` -> `["venue", venueId, "bookings"]`) and the reconnect snapshot
 * invalidate them for free; every live action (arrive/seat/depart/no-show/walk-in) emits a `booking.*`
 * event, so another device's tap refetches here within ~1-2s (deliberately nested under an existing
 * resource rather than adding a "live" one, to keep the shell's single-key resolver contract intact).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SeatRequest, WalkInRequest } from "@repo/api-client";
import { floorApi, floorKeys, type FloorPlanResponse } from "../features/floor-plan/floorApi";
import { liveApi } from "./liveApi";
import { normFloor, normRow, type LiveFloorData, type LiveRow } from "./types";

export const liveKeys = {
  timeline: (venueId: string, serviceDay: string) =>
    ["venue", venueId, "bookings", "live", "timeline", serviceDay] as const,
  floor: (venueId: string) => ["venue", venueId, "bookings", "live", "floor"] as const,
};

/** The venue-TZ service day for "now" as an ISO yyyy-MM-dd (the timeline window key). */
export function todayServiceDay(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function useTimeline(venueId: string, serviceDay: string) {
  return useQuery<LiveRow[]>({
    queryKey: liveKeys.timeline(venueId, serviceDay),
    queryFn: async ({ signal }) =>
      (await liveApi.timeline(venueId, serviceDay, signal)).map(normRow),
    staleTime: 15_000,
  });
}

export function useFloor(venueId: string) {
  return useQuery<LiveFloorData>({
    queryKey: liveKeys.floor(venueId),
    queryFn: async ({ signal }) => normFloor(await liveApi.floor(venueId, signal)),
    staleTime: 15_000,
  });
}

/**
 * The drawn floor plan's GEOMETRY (table x/y, shapes, zones) — the live `/floor` read carries per-table
 * STATE but not coordinates, so the spatial FloorMap joins the two by tableId. Shares the floor-plan
 * feature's query key so the builder's cache and `floor.*` realtime invalidations cover it for free.
 */
export function useFloorGeometry(venueId: string) {
  return useQuery<FloorPlanResponse>({
    queryKey: floorKeys.plan(venueId),
    queryFn: () => floorApi.getPlan(venueId),
    staleTime: 60_000,
  });
}

/** Invalidate the whole bookings family (covers timeline + floor + the dashboard) after a live action. */
function useInvalidateLive(venueId: string): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["venue", venueId, "bookings"] }),
      qc.invalidateQueries({ queryKey: ["venue", venueId, "availability"] }),
    ]);
  };
}

export function useArrive(venueId: string) {
  const invalidate = useInvalidateLive(venueId);
  return useMutation({
    mutationFn: (v: { bookingId: string; version: number }) =>
      liveApi.arrive(venueId, v.bookingId, v.version),
    onSuccess: invalidate,
  });
}

export function useSeat(venueId: string) {
  const invalidate = useInvalidateLive(venueId);
  return useMutation({
    mutationFn: (v: { bookingId: string; body: SeatRequest }) =>
      liveApi.seat(venueId, v.bookingId, v.body),
    onSuccess: invalidate,
  });
}

/** Relocate an already-seated party to different tables (the floor reassign path). */
export function useMove(venueId: string) {
  const invalidate = useInvalidateLive(venueId);
  return useMutation({
    mutationFn: (v: { bookingId: string; body: SeatRequest }) =>
      liveApi.move(venueId, v.bookingId, v.body),
    onSuccess: invalidate,
  });
}

export function useDepart(venueId: string) {
  const invalidate = useInvalidateLive(venueId);
  return useMutation({
    mutationFn: (v: { bookingId: string; version: number }) =>
      liveApi.depart(venueId, v.bookingId, v.version),
    onSuccess: invalidate,
  });
}

export function useNoShow(venueId: string) {
  const invalidate = useInvalidateLive(venueId);
  return useMutation({
    mutationFn: (v: { bookingId: string; version: number }) =>
      liveApi.noShow(venueId, v.bookingId, v.version),
    onSuccess: invalidate,
  });
}

export function useTableReady(venueId: string) {
  const invalidate = useInvalidateLive(venueId);
  return useMutation({
    mutationFn: (v: { tableId: string }) => liveApi.tableReady(venueId, v.tableId),
    onSuccess: invalidate,
  });
}

export function useWalkIn(venueId: string) {
  const invalidate = useInvalidateLive(venueId);
  return useMutation({
    mutationFn: (body: WalkInRequest) => liveApi.walkIn(venueId, body),
    onSuccess: invalidate,
  });
}
